/**
 * Content pipeline — PURE helpers for the Content Engine's studio landing:
 * the type picker, the starter-template gallery, the Idea → Draft →
 * Scheduled → Published kanban, and the analytics summary.
 *
 * PURE by contract: no store, no network, no clock (callers pass `nowMs`
 * where time matters). The persisted record shape (`ContentItem`) lives in
 * src/lib/db/types.ts; typed CRUD lives in src/lib/db/contentItems.ts.
 * Scoring is NOT reimplemented here — the view scores bodies with the
 * shipped scorer in src/lib/crm/contentScore and stamps `item.score`.
 */

import type { ContentItem, ContentItemStatus, ContentItemType } from '../db/types';
import type { Platform } from '../crm/contentScore';
// Type-only import: erased at compile time. Keeps the AQI channel vocabulary
// (linkedin / x_twitter / …) in ONE place — the argoiq engine — while the
// pipeline registry maps each operator-facing type onto it.
import type { ContentChannel } from '../agent/contentEngine';

// ---------------------------------------------------------------------------
// Statuses — the kanban columns, in pipeline order.
// ---------------------------------------------------------------------------

/** Kanban columns in pipeline order. */
export const CONTENT_STATUSES: readonly ContentItemStatus[] = [
  'idea',
  'draft',
  'scheduled',
  'published',
] as const;

export const CONTENT_STATUS_LABELS: Record<ContentItemStatus, string> = {
  idea: 'Idea',
  draft: 'Draft',
  scheduled: 'Scheduled',
  published: 'Published',
};

/** Next stage in the pipeline, or null if already published. */
export function advanceStatus(status: ContentItemStatus): ContentItemStatus | null {
  const i = CONTENT_STATUSES.indexOf(status);
  return i >= 0 && i < CONTENT_STATUSES.length - 1 ? CONTENT_STATUSES[i + 1] : null;
}

/** Previous stage in the pipeline, or null if still an idea. */
export function regressStatus(status: ContentItemStatus): ContentItemStatus | null {
  const i = CONTENT_STATUSES.indexOf(status);
  return i > 0 ? CONTENT_STATUSES[i - 1] : null;
}

// ---------------------------------------------------------------------------
// Content types — the operator-facing picker.
// ---------------------------------------------------------------------------

export interface ContentTypeMeta {
  id: ContentItemType;
  label: string;
  description: string;
  /** Which length norms the shipped scorer should judge this type by. */
  platform: Platform;
  /** AQI channel vocabulary this type publishes/scores against. */
  channel: ContentChannel;
}

export const CONTENT_TYPES: readonly ContentTypeMeta[] = [
  {
    id: 'linkedin-post',
    label: 'LinkedIn post',
    description: 'Narrative thought-leadership, 80–300 words.',
    platform: 'linkedin',
    channel: 'linkedin',
  },
  {
    id: 'x-thread',
    label: 'X thread',
    description: 'Punchy hook tweet + numbered follow-ups.',
    platform: 'x',
    channel: 'x_twitter',
  },
  {
    id: 'x-post',
    label: 'X post',
    description: 'A single post — one sharp idea, ≤280 characters.',
    platform: 'x',
    channel: 'x_twitter',
  },
  {
    id: 'blog',
    label: 'Blog post',
    description: 'Long-form, SEO-ready — pairs with the live SEO editor.',
    platform: 'linkedin',
    channel: 'aeo_blog',
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Subject + short pitch with a single CTA.',
    platform: 'linkedin',
    channel: 'email_newsletter',
  },
  {
    id: 'ig-caption',
    label: 'IG caption',
    description: 'Visual-first caption with a save/share goal.',
    platform: 'x',
    // Closest AQI norms: short, punchy, hook-first — same band as X.
    channel: 'x_twitter',
  },
] as const;

export function contentTypeMeta(type: ContentItemType): ContentTypeMeta {
  const found = CONTENT_TYPES.find((t) => t.id === type);
  // Every ContentItemType is registered above; fall back defensively.
  return found ?? CONTENT_TYPES[0];
}

/** Scorer platform for a content type (the scorer knows 'x' | 'linkedin'). */
export function platformForType(type: ContentItemType): Platform {
  return contentTypeMeta(type).platform;
}

/** AQI channel for a content type — 'x-thread' AND 'x-post' → 'x_twitter'. */
export function channelForType(type: ContentItemType): ContentChannel {
  return contentTypeMeta(type).channel;
}

// ---------------------------------------------------------------------------
// Starter templates — deterministic scaffolds, one click away from a Draft.
// ---------------------------------------------------------------------------

export interface ContentTemplate {
  id: string;
  type: ContentItemType;
  name: string;
  description: string;
  /** Scaffold text with [BRACKETED] slots the operator fills in. */
  body: string;
}

export const CONTENT_TEMPLATES: readonly ContentTemplate[] = [
  {
    id: 'li-lesson',
    type: 'linkedin-post',
    name: 'Hard-won lesson',
    description: 'Scene-first anecdote → one lesson → soft question.',
    body: `Met a [ROLE] last week who [SPECIFIC SITUATION WITH A NUMBER].

Here is what surprised me: [THE COUNTERINTUITIVE PART].

The lesson I took away:
[ONE-SENTENCE LESSON]

What would you have done differently?`,
  },
  {
    id: 'li-listicle',
    type: 'linkedin-post',
    name: 'Saveable list',
    description: 'Number-led list post built for saves and reach.',
    body: `[N] things I learned [DOING SPECIFIC THING] in [TIMEFRAME]:

1. [CONCRETE TAKEAWAY WITH A NUMBER]
2. [CONCRETE TAKEAWAY]
3. [CONCRETE TAKEAWAY]

The one I wish I knew on day one: #[X].`,
  },
  {
    id: 'x-hook-thread',
    type: 'x-thread',
    name: 'Hook + thread',
    description: 'One-line hook tweet, then numbered payoff tweets.',
    body: `I [DID SPECIFIC THING] and [SURPRISING NUMBER RESULT].

Here's the playbook:

1/ [FIRST STEP — concrete, one idea]

2/ [SECOND STEP]

3/ [THIRD STEP]

The part nobody mentions: [HONEST CAVEAT].`,
  },
  {
    id: 'x-contrarian',
    type: 'x-thread',
    name: 'Contrarian take',
    description: 'One quotable declarative built for likes.',
    body: `Everyone says [COMMON ADVICE].

I did the opposite: [WHAT YOU DID, WITH A NUMBER].

[ONE-LINE RESULT].`,
  },
  {
    id: 'x-single-take',
    type: 'x-post',
    name: 'One-idea post',
    description: 'A single ≤280-character post: one claim, one proof.',
    body: `[ONE DECLARATIVE CLAIM].

Proof: [ONE NUMBER OR NAMED RESULT].`,
  },
  {
    id: 'x-single-question',
    type: 'x-post',
    name: 'Sharp question',
    description: 'A single post that ends on a reply-bait question.',
    body: `[SPECIFIC OBSERVATION WITH A NUMBER].

What would you do differently?`,
  },
  {
    id: 'blog-howto',
    type: 'blog',
    name: 'How-to guide',
    description: 'Problem → steps → proof. Finish it in the SEO editor.',
    body: `# How to [ACHIEVE OUTCOME] without [COMMON PAIN]

[WHO THIS IS FOR] keep hitting [SPECIFIC PROBLEM WITH A NUMBER].

## Step 1: [FIRST STEP]
[2–3 sentences of concrete instruction.]

## Step 2: [SECOND STEP]
[2–3 sentences.]

## Step 3: [THIRD STEP]
[2–3 sentences.]

## What happened when we did this
[PROOF: a number, a before/after, a named role.]`,
  },
  {
    id: 'blog-teardown',
    type: 'blog',
    name: 'Teardown / case study',
    description: 'One real example dissected with numbers.',
    body: `# [COMPANY/PERSON] did [THING] — here's the breakdown

The result first: [HEADLINE NUMBER].

## The setup
[Where they started — concrete baseline.]

## What they actually changed
- [CHANGE 1]
- [CHANGE 2]
- [CHANGE 3]

## What you can steal
[ONE actionable takeaway a reader can run this week.]`,
  },
  {
    id: 'email-cold',
    type: 'email',
    name: 'Cold intro',
    description: 'Trigger → one-line value → single soft CTA.',
    body: `Subject: [SPECIFIC TRIGGER] at [COMPANY]

Hi [NAME],

Saw [SPECIFIC TRIGGER — a post, hire, launch, number]. Teams in that spot usually run into [PROBLEM].

We helped [SIMILAR ROLE/COMPANY] get to [NUMBER RESULT] in [TIMEFRAME].

Worth a 15-minute look next week?

[YOUR NAME]`,
  },
  {
    id: 'email-followup',
    type: 'email',
    name: 'Follow-up nudge',
    description: 'References the last touch, asks for one next step.',
    body: `Subject: Re: [PRIOR THREAD]

Hi [NAME],

Circling back on [WHAT YOU DISCUSSED]. Since then, [ONE NEW CONCRETE DATA POINT].

If the timing is still off, say the word and I'll close the loop.

Otherwise — does [DAY/TIME] work?

[YOUR NAME]`,
  },
  {
    id: 'ig-story',
    type: 'ig-caption',
    name: 'Behind-the-scenes',
    description: 'Photo-first caption with a save-worthy payoff.',
    body: `[WHAT THE PHOTO SHOWS — one line, no warm-up].

The part you don't see: [HONEST DETAIL WITH A NUMBER].

Save this if you're [AUDIENCE IN ONE PHRASE].`,
  },
  {
    id: 'ig-tips',
    type: 'ig-caption',
    name: 'Quick tips carousel',
    description: 'Swipe-prompt caption for a multi-slide post.',
    body: `[N] [TOPIC] mistakes I stopped making (swipe →)

1. [MISTAKE → FIX]
2. [MISTAKE → FIX]
3. [MISTAKE → FIX]

Which one are you guilty of?`,
  },
] as const;

/** Starter templates for one content type, in declaration order. */
export function templatesForType(type: ContentItemType): ContentTemplate[] {
  return CONTENT_TEMPLATES.filter((t) => t.type === type);
}

// ---------------------------------------------------------------------------
// Kanban grouping.
// ---------------------------------------------------------------------------

/**
 * Group items into the four kanban columns. Every column is ALWAYS present
 * (empty array when none) so the board renders a stable shape. Within a
 * column, items sort by `updatedAt` descending (freshest first); ties keep
 * input order.
 */
export function groupByStatus(
  items: ContentItem[],
): Record<ContentItemStatus, ContentItem[]> {
  const groups: Record<ContentItemStatus, ContentItem[]> = {
    idea: [],
    draft: [],
    scheduled: [],
    published: [],
  };
  for (const item of items) {
    (groups[item.status] ?? groups.idea).push(item);
  }
  for (const status of CONTENT_STATUSES) {
    groups[status] = [...groups[status]].sort((a, b) =>
      a.updatedAt === b.updatedAt ? 0 : a.updatedAt < b.updatedAt ? 1 : -1,
    );
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Analytics summary.
// ---------------------------------------------------------------------------

export interface ContentAnalytics {
  total: number;
  /** Count per kanban column. Every column present, zero when empty. */
  byStatus: Record<ContentItemStatus, number>;
  /** Count per content type. Every type present, zero when empty. */
  byType: Record<ContentItemType, number>;
  /** Mean of non-null scores, rounded to nearest integer. Null when nothing is scored — never a fabricated 0. */
  avgScore: number | null;
  /** How many items actually carry a score (the honest n behind avgScore). */
  scoredCount: number;
  /** Scheduled items whose `scheduledFor` is today-or-later relative to `nowMs`. */
  upcomingScheduled: number;
}

/**
 * Summarize the pipeline for the analytics panel. Deterministic: pass
 * `nowMs` explicitly in tests; defaults to the current clock in the view.
 */
export function summarizeContent(
  items: ContentItem[],
  nowMs: number = Date.now(),
): ContentAnalytics {
  const byStatus: Record<ContentItemStatus, number> = {
    idea: 0,
    draft: 0,
    scheduled: 0,
    published: 0,
  };
  const byType: Record<ContentItemType, number> = {
    'linkedin-post': 0,
    'x-thread': 0,
    'x-post': 0,
    blog: 0,
    email: 0,
    'ig-caption': 0,
  };
  let scoreSum = 0;
  let scoredCount = 0;
  let upcomingScheduled = 0;

  for (const item of items) {
    if (item.status in byStatus) byStatus[item.status] += 1;
    if (item.type in byType) byType[item.type] += 1;
    if (typeof item.score === 'number' && Number.isFinite(item.score)) {
      scoreSum += item.score;
      scoredCount += 1;
    }
    if (item.status === 'scheduled' && item.scheduledFor) {
      const t = Date.parse(item.scheduledFor);
      if (Number.isFinite(t) && t >= nowMs) upcomingScheduled += 1;
    }
  }

  return {
    total: items.length,
    byStatus,
    byType,
    avgScore: scoredCount > 0 ? Math.round(scoreSum / scoredCount) : null,
    scoredCount,
    upcomingScheduled,
  };
}
